import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface HelpArticle {
  id: string;
  title: string;
  category: string;
  content: string;
  keywords: string[];
  relatedArticles?: string[];
}

export interface GuidedTour {
  id: string;
  name: string;
  description: string;
  steps: TourStep[];
}

export interface TourStep {
  target: string;
  title: string;
  content: string;
  routes?: string[];
}

@Injectable({ providedIn: 'root' })
export class HelpService {
  private drawerOpenSubject = new BehaviorSubject<boolean>(false);
  drawerOpen$ = this.drawerOpenSubject.asObservable();

  readonly categories = [
    { id: 'getting-started', name: 'Getting Started', icon: 'rocket_launch' },
    { id: 'masters', name: 'Master Data', icon: 'storage' },
    { id: 'job-entry', name: 'Job Entry & Orders', icon: 'assignment' },
    { id: 'production', name: 'Production', icon: 'precision_manufacturing' },
    { id: 'challan-invoice', name: 'Challan & Invoicing', icon: 'receipt' },
    { id: 'accounting', name: 'Accounting', icon: 'account_balance' },
    { id: 'reports', name: 'Reports', icon: 'insert_chart' },
    { id: 'admin', name: 'Administration', icon: 'settings' },
  ];

  readonly tours: GuidedTour[] = [
    {
      id: 'quick-overview',
      name: 'Application Overview',
      description: 'Learn how Dentassist manages your dental lab end-to-end',
      steps: [
        { target: '', title: 'Welcome', content: 'Dentassist is a full-stack dental lab management system. It covers everything from job intake to invoicing and payments — with a real-time dashboard to monitor lab performance.' },
        { target: '', title: 'Dashboard', routes: ['/dashboard'], content: 'Your dashboard shows key metrics, monthly trends, configurable late-delivery windows, and top doctor/product summaries so you can focus on the period and exceptions that matter.' },
        { target: '', title: 'Masters', routes: ['/product', '/doctor'], content: 'Start here: set up Products, Processes, Doctors, Price Lists, Shades, HSN/SAC codes, and Checklists. Masters data feeds into every other module, so configure these first.' },
        { target: '', title: 'Job Orders', routes: ['/joborder'], content: 'Create job orders with patient details, product lines, and process routing. Each job gets a unique number and moves through production stages automatically.' },
        { target: '', title: 'Production', routes: ['/jobschedule', '/production-transfer'], content: 'Use Job In to receive jobs into a process and Job Out to mark completion. Supports barcode scanning, rework tracking, and controlled production transfer between employees when work needs to be reassigned.' },
        { target: '', title: 'Challan & Invoice', routes: ['/challan', '/invoice'], content: 'Generate delivery challans when dispatching completed jobs. Create proforma invoices from challans, then convert them to final tax invoices with auto-calculated GST.' },
        { target: '', title: 'Accounting', routes: ['/accounting'], content: 'Record account receipts, create credit/debit notes, post journal vouchers, and use knock-off to match payments against invoices. Track outstanding balances and sales returns.' },
        { target: '', title: 'Audit Trail', content: 'Every record maintains a full audit log — view field-by-field change history from the Audit Log tab on any form. This ensures complete traceability across the system.' },
      ]
    },
    {
      id: 'new-job-flow',
      name: 'New Job Order Flow',
      description: 'Step-by-step: create a job, track production, and invoice',
      steps: [
        { target: '', title: 'Create Job Order', routes: ['/joborder'], content: 'Navigate to Job Orders and click Create. Select a doctor, enter the patient name and procedure, set the delivery date, and choose a financial year.' },
        { target: '', title: 'Add Order Lines', routes: ['/joborder'], content: 'Add products (e.g., PFM Crown) with quantity, shade, and any special remarks. The process route defines which production steps the job will follow.' },
        { target: '', title: 'Save & Track', routes: ['/joborder'], content: 'Save the job order — it receives a unique job number. The status starts as Open and progresses to In Progress, Completed, and Delivered as production advances.' },
        { target: '', title: 'Production', routes: ['/jobschedule', '/production-transfer'], content: 'In Job In, scan or select the job number to receive it into a process. In Job Out, mark each process step as complete, send it to rework when required, or transfer in-progress work to another employee with full audit tracking.' },
        { target: '', title: 'Challan', routes: ['/challan'], content: 'Once all processes are complete, create a challan to dispatch the job to the doctor. Select the completed jobs and shipment type.' },
        { target: '', title: 'Invoice', routes: ['/invoice'], content: 'Create a proforma invoice from one or more challans. Review rates, apply discounts, and generate the final tax invoice. The system auto-calculates SGST/CGST or IGST based on state settings.' },
        { target: '', title: 'Payment', routes: ['/accounting'], content: 'Record the doctor\'s payment as an account receipt. Use knock-off to match the receipt against the invoice and clear the balance.' },
      ]
    }
  ];

  readonly articles: HelpArticle[] = [
    // ==================== GETTING STARTED ====================
    {
      id: 'system-overview',
      title: 'System Overview — Dentassist',
      category: 'getting-started',
      content: `
# Dentassist — Dental Lab Management

**Dentassist** is a comprehensive dental laboratory management system designed to streamline your entire workflow — from receiving job orders to generating invoices.

---

## Core Modules

{{CORE_MODULES_SECTION}}

---

## Key Features

- **Audit Trail** — Every record has a field-level change history. Open any form and check the Audit Log tab to see who changed what and when.
- **Financial Year** — Select the active financial year from the header to scope job orders and invoices.
- **Barcode Scanning** — Scan job barcodes in Job In / Job Out for rapid production tracking.
- **Employee PIN Login** — Employees authenticate with a personal PIN code for production operations.
- **Role-Based Access** — Page-level permissions controlled per user role.
- **Late Delivery Monitoring** — Dashboard users can switch late-delivery windows and review top doctors and products from the same screen.
- **Flexible Person Reports** — Sales / Communication Person reporting supports sales, communication, and combined views with summary or product-wise export.
- **Controlled Status Changes** — Active and inactive updates capture remarks for stronger audit visibility.
- **Role-Scoped Rework Queue** — The home page rework list only shows the jobs allowed for the logged-in role.

---

## Typical Workflow

{{TYPICAL_WORKFLOW_ROWS}}

> **Tip:** Start by setting up your Masters data before creating job orders. This ensures all dropdowns and references are available.
      `,
      keywords: ['overview', 'introduction', 'getting started', 'dental lab', 'workflow', 'dashboard', 'audit'],
    },
    {
      id: 'navigation-guide',
      title: 'Navigating the System',
      category: 'getting-started',
      content: `
# Navigation Guide

## Sidebar Menu

The left sidebar and Help Center show only the modules your role can access:

| Section | Pages |
|---------|-------|
{{NAVIGATION_MENU_ROWS}}

---

## Header Bar

- **Financial Year Selector** — Choose the active financial year from the top bar. This scopes job orders, invoices, and reports to the selected year.
- **Help Button** — The floating help button (bottom-right) opens this Help Center.

---

## Common Actions

### Creating Records
1. Navigate to the relevant page
2. Click the **Create** button in the header
3. Fill in required fields (marked with *)
4. Click **Save**

### Editing Records
1. Click on a record in the list to open it
2. Click the **Edit** button
3. Modify the fields you need
4. Click **Save** or **Discard** to cancel changes

### Viewing Audit History
1. Open any record form
2. Switch to the **Audit Log** tab
3. View the field-by-field change history with timestamps and user info

### Filtering & Sorting
- Use the **search bar** at the top of the list for quick filtering
- Click **column headers** to sort ascending/descending
- Use the **Filter** button for advanced multi-field filtering
- Toggle **Active/Inactive** to view deactivated records

> **Tip:** Combine search and column sorting for the fastest way to find records.
      `,
      keywords: ['navigation', 'sidebar', 'menu', 'filter', 'search', 'create', 'edit'],
    },

    // ==================== MASTERS ====================
    {
      id: 'products',
      title: 'Products & Categories',
      category: 'masters',
      content: `
# Products & Product Categories

## Product Categories
Organize your products into logical categories such as Crowns, Bridges, Dentures, Implants, and Orthodontics.

---

## Products
Each product represents a dental item or service offered by your lab.

### Key Fields

| Field | Description |
|-------|-------------|
| **Name** | Product display name (e.g., "PFM Crown") |
| **SKU** | Unique stock-keeping unit code |
| **Category** | Product category for grouping |
| **HSN/SAC Code** | Tax classification code |
| **Unit** | Measurement unit (per tooth, per unit, etc.) |
| **Rate** | Default rate (can be overridden by price lists) |

### Price Lists
Create doctor-specific or category-specific price lists that override default product rates. This is useful for providing tiered pricing to different doctors.

> **Tip:** Assign a price list to each doctor to automatically apply custom rates when generating invoices.
      `,
      keywords: ['product', 'category', 'sku', 'price', 'rate', 'crown', 'bridge', 'denture'],
      relatedArticles: ['price-lists'],
    },
    {
      id: 'doctors',
      title: 'Doctor Management',
      category: 'masters',
      content: `
# Doctor Management

Doctors are the primary clients of your dental lab. Manage all doctor information in one place.

---

### Basic Information
- **Name** — Doctor's full name
- **Specialization** — Dental specialty
- **Contact** — Phone number, email address

### Addresses
Each doctor can have **multiple addresses** (clinic, hospital, home, etc.).

### Points of Contact
Add multiple contact persons such as receptionists and assistants for each doctor.

### Price Lists
Assign a specific price list to each doctor for automatic custom pricing on invoices.

### Audit Trail
All changes to doctor records are tracked. Open the **Audit Log** tab to see who modified what and when.

---

> **Tip:** Use areas and cities to group doctors geographically — helpful for delivery route planning and report filtering.
      `,
      keywords: ['doctor', 'dentist', 'client', 'address', 'contact', 'poc'],
      relatedArticles: ['products'],
    },
    {
      id: 'processes',
      title: 'Process Management',
      category: 'masters',
      content: `
# Processes

Processes define the production steps a job goes through in your lab.

---

### Common Process Examples

| Process | Description |
|---------|-------------|
| Articulation | Mounting models on articulator |
| Waxing | Wax pattern creation |
| Casting | Metal framework casting |
| Porcelain Build-up | Layering porcelain |
| Finishing | Final polish and adjustments |
| Quality Check | QC inspection before dispatch |
| Packing | Packaging for delivery |

### Process Routing
When creating a job order, define the **sequence of processes**. The job automatically moves through each step as operators mark Job In and Job Out.

### Job Schedule
The Job Schedule page shows all jobs grouped by process, helping lab managers **assign work efficiently**.

> **Tip:** Set up your process sequence once, and it will be reused across all similar job orders — saving time on every order.
      `,
      keywords: ['process', 'production', 'routing', 'step', 'workflow'],
      relatedArticles: ['production-jobin', 'production-jobout'],
    },
    {
      id: 'price-lists',
      title: 'Price Lists',
      category: 'masters',
      content: `
# Price Lists

Price lists allow you to set **custom rates** for products, tailored to specific doctors or groups.

---

### Creating a Price List
1. Go to **Masters → Price List**
2. Click **Create**
3. Enter a descriptive list name
4. Set rates for each product in the pricing table

### Assigning to Doctors
Assign price lists to doctors in the doctor form. When generating invoices, the system automatically uses the doctor's price list rates instead of default product rates.

> **Tip:** Create separate price lists for different tiers of clients — e.g., "Premium Rates", "Bulk Discount Rates", "New Client Rates".
      `,
      keywords: ['price list', 'rate', 'pricing', 'discount', 'custom price'],
      relatedArticles: ['products', 'doctors'],
    },

    // ==================== JOB ENTRY ====================
    {
      id: 'job-orders',
      title: 'Job Orders',
      category: 'job-entry',
      content: `
# Job Orders

Job orders are the **core** of the dental lab workflow. Each job order represents a dental work request from a doctor.

---

### Creating a Job Order
1. Navigate to **Job Orders**
2. Click **Create**
3. Select the **Doctor**
4. Enter **Patient Name** and **Procedure**
5. Add **Order Lines** (products with quantities)
6. Define the **Process Route** (sequence of production steps)
7. Optionally attach documents (prescriptions, photos)
8. Set the delivery date
9. Click **Save**

### Job Number
Each job order automatically receives a **unique job number** for tracking throughout the production cycle.

### Job Status

| Status | Meaning |
|--------|---------|
| **Open** | Job created, not yet in production |
| **In Progress** | Job is currently in production |
| **Completed** | All processes finished |
| **Delivered** | Challan created and dispatched |

### Order Lines
Each order line specifies a product, quantity, shade, and any special remarks.

> **Tip:** Attach prescription photos or documents to the job order — operators can reference them during production from the Job In/Out screen.
      `,
      keywords: ['job order', 'create job', 'patient', 'order line', 'job number'],
      relatedArticles: ['processes', 'production-jobin'],
    },

    // ==================== PRODUCTION ====================
    {
      id: 'production-jobin',
      title: 'Production Job In',
      category: 'production',
      content: `
# Production Job In

Job In records when a job **enters** a production process.

---

### Steps
1. Navigate to **Production → Job In**
2. Select the **Process** you are working on
3. **Scan the barcode** or manually enter the job number
4. View job details (product, doctor, patient)
5. Optionally leave a **comment**
6. Click **Save** to record the job-in timestamp

### Barcode Scanning
Use a barcode scanner to instantly load the job. Place the cursor in the job number field and scan — the job details populate automatically.

### Employee Login
Production operators authenticate with their **personal PIN** code before performing Job In/Out operations. This ensures every action is attributed to the correct employee.

> **Tip:** Barcode scanning is the fastest way to process jobs. Print job labels from the job order form and affix them to the physical work tray.
      `,
      keywords: ['job in', 'production', 'scan', 'process', 'receive'],
      relatedArticles: ['production-jobout', 'processes'],
    },
    {
      id: 'production-jobout',
      title: 'Production Job Out',
      category: 'production',
      content: `
# Production Job Out

Job Out records when a job **leaves** a production process — i.e., the operator has completed their step.

---

### Steps
1. Navigate to **Production → Job Out**
2. **Scan the barcode** or manually select the job number
3. View current process and job details
4. Optionally mark for **Rework** (sends job back to a previous process)
5. Leave a **comment** if needed
6. Click **Save** to record completion

### Rework Flow
If a job needs to be redone:
1. Toggle **Sent to rework**
2. Select the process to send the job back to
3. Enter the rework reason
4. Save — the job will re-enter the selected process and remain visible in **Jobs in Rework** until that rework step is completed again

### Save and Hold
Use **Save and Hold Job** to mark a job as completed but held — for example, when waiting for doctor instructions before proceeding.

### Barcode Scanning
Just like Job In, you can scan the job barcode for instant lookup. The system automatically identifies the current process step for the scanned job.

> **Tip:** The rework feature maintains a full audit trail, so you can always see which jobs were reworked, why they were sent back, and who completed the next step.
      `,
      keywords: ['job out', 'production', 'complete', 'rework', 'hold'],
      relatedArticles: ['production-jobin', 'processes'],
    },
    {
      id: 'production-transfer',
      title: 'Production Transfer',
      category: 'production',
      content: `
# Production Transfer

Production Transfer is used when an in-progress case needs to move from one employee to another without restarting the process.

---

### Common Use Cases
- Shift handover or planned leave
- Urgent work redistribution
- Correcting an incorrect employee assignment
- Balancing department workload

### Steps
1. Navigate to **Production Transfer**
2. Select the **From Employee**
3. Choose one or more active in-progress jobs from **His Jobs IN**
4. Select the **To Employee**
5. Enter the **Transfer Reason**
6. Click **Transfer**

### Notes
- Only users with Production Transfer access can open the screen.
- Employee dropdowns support the standard searchable selection pattern.
- The target employee list automatically excludes the selected source employee.
- Transfers keep the job in its current process and preserve the audit trail.

> **Tip:** Use a clear transfer reason so supervisors can understand why the case changed hands later.
      `,
      keywords: ['production transfer', 'employee transfer', 'handover', 'reassign', 'in progress jobs'],
      relatedArticles: ['production-jobout', 'user-management'],
    },

    // ==================== CHALLAN & INVOICING ====================
    {
      id: 'challans',
      title: 'Challans (Delivery Notes)',
      category: 'challan-invoice',
      content: `
# Challans

Challans (delivery notes) are created when completed jobs are dispatched to doctors.

---

### Creating a Challan
1. Navigate to **Challan**
2. Click **Create**
3. Select the **Company** and **Doctor**
4. Set the **Challan Date**
5. Choose **Completed Jobs** to include
6. Select **Shipment Type**
7. Click **Save**

### Challan to Invoice
Challans are linked to invoices. When creating a proforma or final invoice, you **select which challans** to include — the system pulls in all line items automatically.

### Date Handling
Dates are displayed in **dd MMM yyyy** format throughout the challan view. The system handles timezone conversion automatically — dates entered in the form are stored and displayed correctly regardless of the browser’s timezone.

> **Tip:** Create challans as soon as jobs are dispatched. This keeps your delivery records accurate and speeds up the invoicing process.
      `,
      keywords: ['challan', 'delivery', 'dispatch', 'shipment'],
      relatedArticles: ['proforma-invoice', 'invoice'],
    },
    {
      id: 'proforma-invoice',
      title: 'Proforma Invoice',
      category: 'challan-invoice',
      content: `
# Proforma Invoice

Proforma invoices are **preliminary invoices** sent to doctors for review and confirmation before generating the final tax invoice.

---

### Creating a Proforma
1. Navigate to **Invoicing → Proforma**
2. Click **Create**
3. Select **Doctor**, **Company**, and **Financial Year**
4. Choose **Challans** to include (chips autocomplete)
5. Review line items with job numbers, rates, discounts, and quantities
6. Click **Save**

### Line Items
Each line shows:
- **Job No.** — The job order reference
- **Product** — Dental item/service
- **Quantity** and **Rate**
- **Discount** (per-line percentage)
- **Taxable Amount** — Auto-calculated

### Totals Section
The form displays a clear breakdown:
- **Sub-Total** — Sum of all line items before tax
- **Total Discount** — Sum of discounts applied
- **SGST / CGST / IGST** — Auto-calculated based on state settings
- **Grand Total** — Final amount including all taxes

### Converting to Final Invoice
Once the doctor confirms, click **Generate Invoice** to convert the proforma into an official tax invoice.

> **Tip:** You can apply line-level discounts on the proforma before generating the final invoice.
      `,
      keywords: ['proforma', 'preliminary', 'invoice', 'estimate'],
      relatedArticles: ['challans', 'invoice'],
    },
    {
      id: 'invoice',
      title: 'Final Invoice (Tax Invoice)',
      category: 'challan-invoice',
      content: `
# Tax Invoice

Final invoices are the official tax invoices generated for accounting and payment purposes.

---

### Creating from Proforma
1. Open a confirmed proforma invoice
2. Click **Generate Invoice**
3. The system creates a final invoice with all details carried over

### Invoice Details

| Component | Description |
|-----------|-------------|
| **Line Items** | Product-wise breakdown with job numbers and rates |
| **Discounts** | Per-line and overall discounts |
| **Sub-Total** | Sum before taxes |
| **Taxes** | Auto-calculated SGST, CGST, or IGST |
| **Grand Total** | Final payable amount |
| **Invoice No.** | Auto-generated sequential number |

### Tax Calculation
The system automatically determines the tax type:
- **Same state** (company and doctor) → SGST + CGST
- **Different state** → IGST

### Printing
Use the **Print** button to generate a downloadable invoice PDF.

### Audit Trail
All invoice changes are tracked. Open the **Audit Log** tab to see the full history of modifications.

> **Tip:** The system automatically determines whether to apply SGST+CGST or IGST based on the company and doctor state settings.
      `,
      keywords: ['invoice', 'tax invoice', 'gst', 'billing', 'payment'],
      relatedArticles: ['proforma-invoice', 'challans'],
    },

    // ==================== ACCOUNTING ====================
    {
      id: 'accounts',
      title: 'Account Management',
      category: 'accounting',
      content: `
# Accounts

The Accounts module manages the **chart of accounts** for financial tracking across your lab.

---

### Account Types

| Type | Description | Examples |
|------|-------------|----------|
| **Cash** | Physical cash accounts | Petty cash, cash in hand |
| **Bank** | Bank accounts | Current account, savings account |
| **Other** | All other account heads | Revenue, expense, asset, liability |

### Features
- Create and manage account heads with type classification
- View account balances
- Track transactions per account
- Assign accounts to receipts, notes, and journal vouchers

### Account Groups
Accounts are organized into groups for reporting and classification. Each account belongs to a group that determines its behavior in financial statements.

> **Tip:** Set up your chart of accounts during initial setup to ensure all financial transactions are properly categorized from day one.
      `,
      keywords: ['account', 'chart of accounts', 'ledger', 'balance'],
      relatedArticles: ['account-receipt', 'journal-voucher'],
    },
    {
      id: 'account-receipt',
      title: 'Account Receipts',
      category: 'accounting',
      content: `
# Account Receipts

Record payments received from doctors against outstanding invoices.

---

### Creating a Receipt
1. Navigate to **Accounting → Account Receipt**
2. Click **Create**
3. Select the **Company** and **Doctor**
4. Choose the **Account** (cash or bank) receiving the payment
5. Enter payment details — amount, receipt date, payment mode, reference number
6. Click **Save**

### Receipt States

| State | Meaning |
|-------|--------|
| **Create** | New receipt — fill in details and save |
| **View** | Saved receipt — read-only view |
| **Edit** | Click Edit to modify an existing receipt |

### Knock-Off Tab
After saving a receipt, switch to the **Knock-Off** tab to match the payment against specific outstanding invoices:
1. Click the **Knock-Off** tab
2. The system shows all outstanding invoices for the selected doctor
3. Enter knock-off amounts against invoices
4. Save — the balances update immediately

The remaining balance after knock-off is shown at the top of the tab.

### Audit Trail
Switch to the **Audit Log** tab to view the complete change history for any receipt — including who modified it and what fields were changed.

> **Tip:** Always knock off receipts promptly to keep the doctor ledger up to date.
      `,
      keywords: ['receipt', 'payment', 'collection', 'money received'],
      relatedArticles: ['accounts', 'knock-off', 'notes'],
    },
    {
      id: 'notes',
      title: 'Notes (Credit / Debit)',
      category: 'accounting',
      content: `
# Credit & Debit Notes

Notes are used to adjust balances when an invoice amount needs correction — either upward (debit note) or downward (credit note).

---

### Types

| Type | When to Use |
|------|-------------|
| **Credit Note** | Reduce a doctor's outstanding balance (e.g., goods returned, pricing error in doctor's favor) |
| **Debit Note** | Increase a doctor's outstanding balance (e.g., additional charges, corrections) |

### Creating a Note
1. Navigate to **Accounting → Notes**
2. Click **Create**
3. Select **Note Type** (Credit or Debit)
4. Select the **Company** and **Doctor**
5. Choose the **Account** for the adjustment
6. Enter the voucher date, amount, and description
7. Click **Save**

### Knock-Off Tab
Just like account receipts, notes have a **Knock-Off** tab. After saving:
1. Switch to the **Knock-Off** tab
2. Match the note amount against outstanding invoices
3. Save — balances update immediately

### Audit Trail
All note changes are tracked in the **Audit Log** tab.

> **Tip:** Use credit notes for returns or refunds, and debit notes for additional charges. Always knock off against the relevant invoices to keep balances accurate.
      `,
      keywords: ['note', 'credit note', 'debit note', 'adjustment', 'return'],
      relatedArticles: ['account-receipt', 'knock-off', 'accounts'],
    },
    {
      id: 'knock-off',
      title: 'Knock-Off',
      category: 'accounting',
      content: `
# Knock-Off

Knock-off matches receipts and payments against outstanding invoices to **clear balances** accurately.

---

### How It Works
Knock-off is accessed from the **Knock-Off tab** on Account Receipts and Credit/Debit Notes:
1. Open a saved receipt or note
2. Switch to the **Knock-Off** tab
3. The system lists all outstanding invoices for the selected doctor
4. Enter the amount to knock off against each invoice
5. Save — balances update immediately

### Balance Tracking
After each knock-off, the system recalculates and displays:
- **Total receipt/note amount**
- **Total knocked-off amount**
- **Remaining balance**

Dates in the knock-off list are displayed in **dd MMM yyyy** format (e.g., 15 Jan 2025) for clarity.

### Standalone Knock-Off Page
You can also access knock-off from **Accounting → Knock-Off** to view all knock-off records across doctors.

> **Tip:** Review the Account Aging report regularly to identify doctors with overdue balances.
      `,
      keywords: ['knock off', 'match', 'allocation', 'outstanding', 'clear balance'],
      relatedArticles: ['account-receipt', 'accounts'],
    },
    {
      id: 'journal-voucher',
      title: 'Journal Voucher',
      category: 'accounting',
      content: `
# Journal Voucher

Journal vouchers record **manual accounting adjustments** that don't fit into standard receipt or payment flows.

---

### When to Use
- Correcting entries
- Write-offs
- Adjustments between accounts
- Opening balance entries

### Creating a Journal Voucher
1. Navigate to **Accounting → Journal Voucher**
2. Click **Create**
3. Select the **Company** and set the **voucher date**
4. Add debit and credit lines (each referencing an account)
5. The total debits and credits must balance
6. Add a narration/description
7. Click **Save**

### Audit Trail
Like all accounting records, journal vouchers maintain a full audit log. Switch to the **Audit Log** tab to view the history of changes.

> **Tip:** Always add a clear narration/description to journal vouchers for future audit reference.
      `,
      keywords: ['journal', 'voucher', 'adjustment', 'debit', 'credit', 'entry'],
      relatedArticles: ['accounts'],
    },

    // ==================== REPORTS ====================
    {
      id: 'reports-overview',
      title: 'Reports Overview',
      category: 'reports',
      content: `
# Reports

Dentassist provides a suite of **analytical reports** to help you monitor lab performance and financials.

---

| Report | Description |
|--------|-------------|
| **Pending Job Data** | Jobs currently in production with process status |
| **Account Aging** | Outstanding balances aged by period (30/60/90 days) |
| **Job Order Log** | Complete history of all job orders |
| **All Doctors** | Doctor-wise summary and analytics |
| **Doctor Business Summary** | Revenue and business contribution summary by doctor |
| **Product Usage Summary** | Product mix and quantity trends across the selected period |
| **Sales / Communication Person** | Sales, communication, or combined performance with summary and product-wise views |
| **Production Details** | Process-wise production throughput data |
| **Daily Job Activity** | Daily summary of job in/out activity |
| **Tax Invoice** | Invoice generation and printing |
| **Doctor Ledger** | Complete account statement for each doctor |

### Using Reports
1. Navigate to **Reports**
2. Select the report type
3. Apply filters (date range, doctor, category, financial year, month range, person type, etc.)
4. View the data or export it

### Exporting
Most reports support exporting to Excel or PDF. Use the export button in the report toolbar.

### Recent Enhancements
- Dashboard summaries highlight late deliveries by selected aging window.
- Sales / Communication Person reporting supports **Sales**, **Communication**, and **Both** modes.
- Person performance reports support **Summary** and **Product-wise Summary** output for easier review and Excel sharing.

> **Tip:** Use the Account Aging report weekly to follow up on overdue payments and maintain healthy cash flow.
      `,
  keywords: ['report', 'analytics', 'export', 'pending', 'aging', 'ledger', 'excel', 'doctor business summary', 'product usage summary', 'sales communication'],
    },

    // ==================== ADMIN ====================
    {
      id: 'user-management',
      title: 'User & Role Management',
      category: 'admin',
      content: `
# User & Role Management

Control who has access to what in Dentassist with **role-based access control**.

---

### User Roles
Create roles with specific permissions:
1. Navigate to **Admin → User Roles**
2. Click **Create**
3. Name the role (e.g., "Lab Technician", "Admin", "Accountant")
4. Save

### Access Control
Assign page-level permissions to each role:
1. Navigate to **Admin → Access Control**
2. Select a role
3. Toggle permissions for each page and action (View, Create, Edit, Delete)
4. Save

### Role-Aware Help & Navigation
- The sidebar shows only the modules the selected role can access.
- The Help Center also hides modules and guided tour steps that are not available to that role.
- Admin users can review the full application guidance because their role has access to all modules.

### Users
Create user accounts and assign roles:
1. Navigate to **Admin → Users**
2. Click **Create**
3. Enter username, email, and password
4. Assign a role
5. Save

---

### Company Settings

| Setting | Description |
|---------|-------------|
| **Name** | Company display name |
| **Address** | Registered address |
| **GST Number** | GSTIN for tax invoices |
| **State** | State (determines SGST/CGST vs IGST) |
| **Logo** | Company logo for invoices and reports |

### Employees & PIN Login
Manage lab employees who perform production tasks:
- Create employees with name, department, and contact details
- Each employee has a unique **4-digit PIN** for authentication
- Employees use their PIN to log in at production stations (Job In/Out)
- PIN validation ensures uniqueness across all employees

### Departments
Organize employees into departments for better workforce management and reporting.

### Financial Year
Set up financial year periods. The active financial year is selectable from the header bar and scopes all date-dependent records.

### Status Change Remarks
When activating or deactivating master records, capture a clear status remark so the audit trail explains why the record changed.

> **Tip:** Create specific roles for different job functions (e.g., Lab Tech, Accountant, Manager) to maintain data security and simplify training.
      `,
      keywords: ['user', 'role', 'permission', 'access', 'admin', 'employee', 'company'],
    },
  ];

  // ==================== PUBLIC METHODS ====================

  openDrawer(): void {
    this.drawerOpenSubject.next(true);
  }

  closeDrawer(): void {
    this.drawerOpenSubject.next(false);
  }

  toggleDrawer(): void {
    this.drawerOpenSubject.next(!this.drawerOpenSubject.value);
  }

  searchArticles(query: string): HelpArticle[] {
    const lower = query.toLowerCase();
    return this.articles.filter(article =>
      article.title.toLowerCase().includes(lower) ||
      article.content.toLowerCase().includes(lower) ||
      article.keywords.some(kw => kw.toLowerCase().includes(lower))
    );
  }

  getArticlesByCategory(categoryId: string): HelpArticle[] {
    return this.articles.filter(a => a.category === categoryId);
  }

  getArticleById(id: string): HelpArticle | undefined {
    return this.articles.find(a => a.id === id);
  }

  // ── Active tour state ──
  activeTour: GuidedTour | null = null;
  activeTourStep = 0;

  startTour(tourId: string, tourOverride?: GuidedTour): void {
    const tour = tourOverride || this.tours.find(t => t.id === tourId);
    if (tour) {
      this.activeTour = tour;
      this.activeTourStep = 0;
    }
  }

  nextTourStep(): void {
    if (this.activeTour && this.activeTourStep < this.activeTour.steps.length - 1) {
      this.activeTourStep++;
    }
  }

  prevTourStep(): void {
    if (this.activeTourStep > 0) {
      this.activeTourStep--;
    }
  }

  endTour(): void {
    this.activeTour = null;
    this.activeTourStep = 0;
  }
}
